/**
 * Onboarding Service
 *
 * Service for managing user onboarding status in Firestore
 * Handles CRUD operations for onboarding completion tracking
 */

import { getFirestoreClient } from "../lib/firestoreClient";
import type { Result } from "../types/result";
import { Ok } from "../types/result";
import type { AppError } from "../types/errors";
import { createLogger } from "../utils/logger";

const onboardingServiceLogger = createLogger({ prefix: "OnboardingService" });

/**
 * Onboarding status document structure
 */
export interface OnboardingStatus {
  welcomeViewed: boolean;
  welcomeViewedAt: Date | null;
  completed: boolean;
  completedAt: Date | null;
}

/**
 * Internal Firestore representation (with timestamp-like objects)
 */
interface OnboardingStatusFirestore {
  welcomeViewed: boolean;
  welcomeViewedAt: { toDate(): Date } | null;
  completed: boolean;
  completedAt: { toDate(): Date } | null;
}

/**
 * Get onboarding status for a user
 *
 * @param userId - User ID
 * @returns Result containing the onboarding status or null if not found
 */
export async function getOnboardingStatus(
  userId: string
): Promise<Result<OnboardingStatus | null, AppError>> {
  onboardingServiceLogger.debug("Fetching onboarding status", { userId });

  const client = getFirestoreClient();
  const path = ["users", userId, "onboarding", "status"];

  const onboardingDoc = await client.getDocument(path);

  if (!onboardingDoc.exists()) {
    onboardingServiceLogger.debug("Onboarding status not found", { userId });
    return Ok(null);
  }

  const data = onboardingDoc.data() as unknown as OnboardingStatusFirestore;

  const parsedData: OnboardingStatus = {
    welcomeViewed: data.welcomeViewed ?? false,
    welcomeViewedAt: data.welcomeViewedAt
      ? (data.welcomeViewedAt as { toDate(): Date }).toDate()
      : null,
    completed: data.completed ?? false,
    completedAt: data.completedAt
      ? (data.completedAt as { toDate(): Date }).toDate()
      : null,
  };

  return Ok(parsedData);
}

/**
 * Mark onboarding welcome screen as viewed.
 * Enqueues the write — the queue auto-flushes on timer / page-hide.
 *
 * @param userId - User ID
 * @returns Result indicating success or error
 */
export async function markWelcomeViewed(
  userId: string
): Promise<Result<void, AppError>> {
  onboardingServiceLogger.debug("Marking welcome as viewed", { userId });

  const client = getFirestoreClient();
  const path = ["users", userId, "onboarding", "status"];

  await client.setDocument(
    path,
    {
      welcomeViewed: true,
      welcomeViewedAt: client.serverTimestamp(),
    } as Record<string, unknown>,
    { merge: true }
  );

  onboardingServiceLogger.info("Welcome marked as viewed", { userId });
  return Ok(undefined);
}

/**
 * Mark onboarding as complete for a user.
 * Enqueues the write — the queue auto-flushes on timer / page-hide.
 *
 * @param userId - User ID
 * @returns Result indicating success or error
 */
export async function completeOnboarding(
  userId: string
): Promise<Result<void, AppError>> {
  onboardingServiceLogger.debug("Completing onboarding", { userId });

  const client = getFirestoreClient();
  const path = ["users", userId, "onboarding", "status"];

  await client.setDocument(
    path,
    {
      completed: true,
      completedAt: client.serverTimestamp(),
    } as Record<string, unknown>,
    { merge: true }
  );

  onboardingServiceLogger.info("Onboarding completed", { userId });
  return Ok(undefined);
}
